import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import type { Job, Reference } from "../lib/types";
import * as api from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useWebSocket } from "../hooks/useWebSocket";

const WS_AVAILABLE = !!(import.meta.env.VITE_WS_URL);
const POLL_INTERVAL_WS = 5_000;
const POLL_INTERVAL_NO_WS = 2_000;
const EXIT_ANIMATION_MS = 250;

interface DataCacheContextType {
  // Jobs
  jobs: Job[];
  jobsLoading: boolean;
  jobsError: string | null;
  refreshJobs: () => Promise<void>;
  loadMoreJobs: () => Promise<void>;
  hasMoreJobs: boolean;
  optimisticDelete: (jobId: string) => void;
  optimisticBatchRemove: (jobIds: string[]) => void;
  optimisticCancel: (jobId: string) => void;
  optimisticInsert: (job: Partial<Job> & { jobId: string }) => void;
  averageGenTime: number | undefined;

  // References
  references: Reference[];
  providedRefs: Reference[];
  refsLoading: boolean;
  refreshRefs: () => Promise<void>;
}

const DataCacheContext = createContext<DataCacheContextType | null>(null);

export function useDataCache(): DataCacheContextType {
  const ctx = useContext(DataCacheContext);
  if (!ctx) throw new Error("useDataCache must be used within DataCacheProvider");
  return ctx;
}

export function DataCacheProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  // ── Jobs state ──────────────────────────────────────────────────────
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [jobsCursor, setJobsCursor] = useState<string | undefined>();
  const pollingRef = useRef<ReturnType<typeof setInterval>>();

  const sortByCreatedAt = (a: Job, b: Job) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  // The API mints a fresh presigned URL on every list/get call. Each one
  // points to the same S3 object but has a different signature querystring,
  // so React would see <audio src=...> change on every poll and tear down
  // the playback session. Preserve the old URL when outputKey is unchanged
  // — same trick for reference audio URLs.
  const preserveAudioUrls = (prev: Job | undefined, next: Job): Job => {
    if (!prev) return next;
    let audioUrl = next.audioUrl;
    if (
      audioUrl && prev.audioUrl &&
      prev.outputKey && next.outputKey &&
      prev.outputKey === next.outputKey
    ) {
      audioUrl = prev.audioUrl;
    }
    let referenceAudioUrl = next.referenceAudioUrl;
    if (
      referenceAudioUrl && prev.referenceAudioUrl &&
      prev.referenceKey && next.referenceKey &&
      prev.referenceKey === next.referenceKey
    ) {
      referenceAudioUrl = prev.referenceAudioUrl;
    }
    const prevTakeByIdx = new Map(
      (prev.takes ?? []).map((t) => [t.takeIndex, t]),
    );
    const takes = next.takes?.map((t) => {
      const p = prevTakeByIdx.get(t.takeIndex);
      if (
        p && p.audioUrl && t.audioUrl &&
        p.outputKey && t.outputKey &&
        p.outputKey === t.outputKey
      ) {
        return { ...t, audioUrl: p.audioUrl };
      }
      return t;
    });
    return { ...next, audioUrl, referenceAudioUrl, ...(takes ? { takes } : {}) };
  };

  const fetchJobs = useCallback(async () => {
    try {
      const result = await api.listJobs(25);
      setJobs((prev) => {
        const apiMap = new Map(result.items.map((j) => [j.jobId, j]));
        // Keep any local-only jobs (optimistically inserted, not yet in API response)
        const localOnly = prev.filter(
          (j) => !apiMap.has(j.jobId) && (j.status === "queued" || j.status === "processing")
        );
        // Merge: API data wins for existing jobs, but preserve _prevStatus/_exiting flags
        const merged = result.items.map((apiJob) => {
          const existing = prev.find((j) => j.jobId === apiJob.jobId);
          if (existing) {
            const _prevStatus =
              existing.status !== apiJob.status ? existing.status : existing._prevStatus;
            const stable = preserveAudioUrls(existing, apiJob);
            return { ...existing, ...stable, _prevStatus };
          }
          return apiJob;
        });
        return [...localOnly, ...merged].sort(sortByCreatedAt);
      });
      setJobsCursor(result.cursor);
      setJobsError(null);
    } catch (err) {
      setJobsError(err instanceof Error ? err.message : "Failed to load jobs");
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const loadMoreJobs = useCallback(async () => {
    if (!jobsCursor) return;
    try {
      const result = await api.listJobs(25, jobsCursor);
      setJobs((prev) => [...prev, ...result.items].sort(sortByCreatedAt));
      setJobsCursor(result.cursor);
    } catch (err) {
      setJobsError(err instanceof Error ? err.message : "Failed to load more");
    }
  }, [jobsCursor]);

  const optimisticDelete = useCallback((jobId: string) => {
    setJobs((prev) =>
      prev.map((j) => (j.jobId === jobId ? { ...j, _exiting: true } : j))
    );
    setTimeout(() => {
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
    }, EXIT_ANIMATION_MS);

    let snapshot: Job | undefined;
    setJobs((prev) => {
      snapshot = prev.find((j) => j.jobId === jobId);
      return prev;
    });

    api.deleteJob(jobId).catch(() => {
      if (snapshot) {
        const reverted = { ...snapshot, _exiting: false };
        setJobs((prev) => [...prev, reverted].sort(sortByCreatedAt));
      }
    });
  }, []);

  const optimisticBatchRemove = useCallback((jobIds: string[]) => {
    const idSet = new Set(jobIds);
    setJobs((prev) =>
      prev.map((j) => (idSet.has(j.jobId) ? { ...j, _exiting: true } : j))
    );
    setTimeout(() => {
      setJobs((prev) => prev.filter((j) => !idSet.has(j.jobId)));
    }, EXIT_ANIMATION_MS);
  }, []);

  const optimisticCancel = useCallback((jobId: string) => {
    setJobs((prev) =>
      prev.map((j) => {
        if (j.jobId === jobId) {
          return { ...j, status: "cancelled" as const, _prevStatus: j.status };
        }
        return j;
      })
    );

    api.cancelJob(jobId).catch(() => {
      setJobs((prev) =>
        prev.map((j) => {
          if (j.jobId === jobId && j._prevStatus) {
            return { ...j, status: j._prevStatus as Job["status"], _prevStatus: undefined };
          }
          return j;
        })
      );
    });
  }, []);

  // Optimistic insert — adds a placeholder job immediately after submission
  const optimisticInsert = useCallback((partial: Partial<Job> & { jobId: string }) => {
    const placeholder: Job = {
      userId: "",
      type: "tts",
      status: "queued",
      progress: 0,
      referenceName: "",
      referenceKey: "",
      createdAt: new Date().toISOString(),
      ttl: 0,
      ...partial,
    };
    setJobs((prev) => {
      if (prev.some((j) => j.jobId === placeholder.jobId)) return prev;
      return [placeholder, ...prev].sort(sortByCreatedAt);
    });
  }, []);

  // WebSocket handler
  const handleJobUpdate = useCallback((updatedJob: Job) => {
    setJobs((prev) => {
      const exists = prev.some((j) => j.jobId === updatedJob.jobId);
      let next: Job[];
      if (exists) {
        next = prev.map((j) => {
          if (j.jobId === updatedJob.jobId) {
            const prevStatus = j.status;
            const newStatus = updatedJob.status;
            const _prevStatus = prevStatus !== newStatus ? prevStatus : j._prevStatus;
            const stable = preserveAudioUrls(j, updatedJob);
            return { ...j, ...stable, _prevStatus };
          }
          return j;
        });
      } else {
        next = [updatedJob, ...prev];
      }
      return next.sort(sortByCreatedAt);
    });

    // Trigger a full refetch whenever the WS push delivered a complete output
    // (legacy outputKey OR any complete take) without a presigned audioUrl —
    // DDB Streams gives us the raw row, so URLs have to come from the API.
    const needsLegacyUrl =
      updatedJob.status === "complete" &&
      !!updatedJob.outputKey &&
      !updatedJob.audioUrl;
    const needsTakeUrls =
      (updatedJob.takes ?? []).some(
        (t) => t.status === "complete" && t.outputKey && !t.audioUrl
      );

    if (needsLegacyUrl || needsTakeUrls) {
      api.getJob(updatedJob.jobId).then((fullJob) => {
        setJobs((prev) =>
          prev.map((j) =>
            j.jobId === fullJob.jobId
              ? { ...j, ...preserveAudioUrls(j, fullJob) }
              : j
          ).sort(sortByCreatedAt)
        );
      }).catch(() => {});
    }
  }, []);

  useWebSocket({ onJobUpdate: handleJobUpdate, enabled: WS_AVAILABLE });

  // Active job polling
  useEffect(() => {
    const activeJobs = jobs.filter(
      (j) => j.status === "queued" || j.status === "processing"
    );

    if (activeJobs.length === 0) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = undefined;
      }
      return;
    }

    const interval = WS_AVAILABLE ? POLL_INTERVAL_WS : POLL_INTERVAL_NO_WS;

    pollingRef.current = setInterval(async () => {
      try {
        const updated = await Promise.all(
          activeJobs.map((j) => api.getJob(j.jobId).catch(() => j))
        );
        setJobs((prev) =>
          prev.map((job) => {
            const fresh = updated.find((u) => u.jobId === job.jobId);
            if (!fresh) return job;
            return { ...job, ...preserveAudioUrls(job, fresh) };
          }).sort(sortByCreatedAt)
        );
      } catch {}
    }, interval);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [jobs]);

  // Average gen time
  const averageGenTime = (() => {
    const completed = jobs.filter(
      (j) => j.status === "complete" && j.genTimeSeconds && j.genTimeSeconds > 0
    );
    if (completed.length === 0) return undefined;
    const total = completed.reduce((sum, j) => sum + (j.genTimeSeconds ?? 0), 0);
    return total / completed.length;
  })();

  // ── References state ────────────────────────────────────────────────
  const [references, setReferences] = useState<Reference[]>([]);
  const [providedRefs, setProvidedRefs] = useState<Reference[]>([]);
  const [refsLoading, setRefsLoading] = useState(true);

  const fetchRefs = useCallback(async () => {
    try {
      const result = await api.listReferences();
      setReferences(result.items);
      setProvidedRefs(result.provided ?? []);
    } catch {
      // silently fail — individual pages can show their own error
    } finally {
      setRefsLoading(false);
    }
  }, []);

  // ── Initial load on mount ───────────────────────────────────────────
  useEffect(() => {
    if (user) {
      fetchJobs();
      fetchRefs();
    }
  }, [user, fetchJobs, fetchRefs]);

  return (
    <DataCacheContext.Provider
      value={{
        jobs,
        jobsLoading,
        jobsError,
        refreshJobs: fetchJobs,
        loadMoreJobs,
        hasMoreJobs: !!jobsCursor,
        optimisticDelete,
        optimisticBatchRemove,
        optimisticCancel,
        optimisticInsert,
        averageGenTime,
        references,
        providedRefs,
        refsLoading,
        refreshRefs: fetchRefs,
      }}
    >
      {children}
    </DataCacheContext.Provider>
  );
}
